import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  budgetKeys,
  characterKeys,
  coverageKeys,
  projectKeys,
  scheduleKeys,
  scriptKeys,
} from "queries";
import { sessionKeys } from "queries/profile.keys";
import { useAxios } from "hooks/useAxios";
import { getUseProject } from "hooks/useProjects/useProjectList";
import type { ProjectState } from "hooks/useProjects/useProjects.types";
import type { AppState } from "store/reducers/Reducer";
import { FEATURE_STATUS_COMPLETED } from "config";
import { resolveEffectiveBudgetFeatureStatus } from "components/dashboard/body/projects/panels/budget/budgetAiRunState";
import { handleToastWithAction, ToastActionButton } from "utils/toasts";
import { setActiveAiRun } from "./activeAiRunsStore";
import {
  formatAvailabilityConflictSentence,
  getScheduleAvailabilityConflictCount,
  getScheduleAvailabilityConflictExamples,
} from "utils/aiRunMergePolicy";
import {
  aiRunFailureReason,
  aiRunFeatureLabel,
  aiRunFeaturePanelPath,
  detectAiRunTransitions,
  hasForegroundAiWatcher,
  isActiveAiRunStatus,
} from "utils/aiRunTransitions";
import {
  acceptedRunEnd,
  claimBudgetRunEnd,
  endsAcceptedBudgetRun,
  readAcceptedBudgetRun,
} from "hooks/budget/acceptedBudgetRun";
import { clearTargetedRerunCategories } from "hooks/budget/targetedRerunNames";

/** The run's end is handled here, so its saved targeted-rerun names are no longer needed. */
const clearSavedCategoryNames = (projectId: string, documentId?: string) => {
  if (documentId) clearTargetedRerunCategories(projectId, documentId);
};

/** How long a new watch survives quiet polls, from when it was added. */
const MIN_WATCH_MS = 30_000;

interface WatchedProject {
  projectId: string;
  documentId?: string;
}

interface FeatureStatusPayloadItem {
  feature?: string;
  status?: string;
}

const payloadHasActiveRun = (data: unknown): boolean => {
  const rows =
    (data as { feature_statuses?: FeatureStatusPayloadItem[] } | null | undefined)
      ?.feature_statuses ??
    (data as { featureStatuses?: FeatureStatusPayloadItem[] } | null | undefined)?.featureStatuses;
  if (!Array.isArray(rows)) return false;
  return rows.some((row) => isActiveAiRunStatus(row?.status));
};

/**
 * Keeps AI-run status polling alive for any project with an active run, no
 * matter which route is mounted, and reports completions via toasts when no
 * panel provider is watching that project. Fixes the "navigate away and the
 * run finishes silently" gap.
 *
 * The watch list belongs to the signed-in account. It sits above the routes and
 * survives logout, so it remounts per user: the next account must not keep
 * polling (or showing) the previous account's projects.
 */
export const GlobalAiRunWatcher: React.FC = () => {
  const userId = useSelector((state: AppState) => state.auth.user_id);
  return <AccountAiRunWatcher key={userId || "signed-out"} />;
};

const AccountAiRunWatcher: React.FC = () => {
  const queryClient = useQueryClient();
  const [watched, setWatched] = useState<Record<string, WatchedProject>>({});

  const addWatch = useCallback((projectId: string, documentId?: string) => {
    if (!projectId || projectId === "0" || projectId === "undefined") return;
    setWatched((prev) => {
      const existing = prev[projectId];
      if (existing && (existing.documentId === documentId || !documentId)) return prev;
      return {
        ...prev,
        [projectId]: { projectId, documentId: documentId ?? existing?.documentId },
      };
    });
  }, []);

  const removeWatch = useCallback((projectId: string) => {
    setWatched((prev) => {
      if (!prev[projectId]) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }, []);

  // Discover projects with active runs from the query cache: the optimistic
  // pipeline lock (set the moment a run is enqueued) and any project detail
  // payload carrying an active feature status (covers reload rehydration once
  // the project is fetched anywhere).
  useEffect(() => {
    const scan = (queryKey: unknown, data: unknown) => {
      if (!Array.isArray(queryKey) || data == null) return;
      if (queryKey[0] === "scriptAiPipelineLock" && queryKey.length >= 3 && data === true) {
        addWatch(String(queryKey[1]), String(queryKey[2]));
        return;
      }
      if (queryKey[0] === "projects" && queryKey[1] === "detail" && queryKey[2] != null) {
        if (payloadHasActiveRun(data)) {
          const scope = queryKey[3] as { documentId?: string } | undefined;
          addWatch(String(queryKey[2]), scope?.documentId ? String(scope.documentId) : undefined);
        }
      }
    };

    // Seed from whatever is already cached (e.g. right after a reload).
    for (const query of queryClient.getQueryCache().getAll()) {
      scan(query.queryKey, query.state.data);
    }

    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" && event.type !== "added") return;
      scan(event.query.queryKey, event.query.state.data);
    });
    return unsubscribe;
  }, [addWatch, queryClient]);

  return (
    <>
      {Object.values(watched).map((entry) => (
        <ProjectAiRunPoller
          key={`${entry.projectId}:${entry.documentId ?? ""}`}
          projectId={entry.projectId}
          documentId={entry.documentId}
          onIdle={removeWatch}
        />
      ))}
    </>
  );
};

const ProjectAiRunPoller: React.FC<{
  projectId: string;
  documentId?: string;
  onIdle: (projectId: string) => void;
}> = ({ projectId, documentId, onIdle }) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Same hook/key as the mounted app pollers, so React Query dedupes requests
  // instead of double-fetching when a workspace poller is also active.
  const useProject = getUseProject(axios);
  const { data: project, dataUpdatedAt } = useProject(projectId, true, documentId);

  const prevStatusesRef = useRef<Record<string, string | undefined>>({});
  const idlePollsRef = useRef(0);
  const lastQuietFetchRef = useRef<number | null>(null);
  const watchedSinceRef = useRef(Date.now());

  // Publish live run state for the indicator pill and project-list chips.
  useEffect(() => {
    if (!project?.featureStatuses) return;
    const features = project.featureStatuses
      .filter((row) => isActiveAiRunStatus(row.status))
      .map((row) => row.feature);
    setActiveAiRun(
      projectId,
      features.length
        ? {
            projectId,
            projectName: project.name,
            workspaceId: project.workspaceId,
            features,
          }
        : null
    );
  }, [project, projectId]);

  useEffect(() => {
    return () => setActiveAiRun(projectId, null);
  }, [projectId]);

  useEffect(() => {
    if (!project?.featureStatuses?.length) return;

    const transitions = detectAiRunTransitions(prevStatusesRef.current, project.featureStatuses);

    for (const transition of transitions) {
      if (hasForegroundAiWatcher(projectId, transition.feature)) {
        // A mounted panel provider already toasts and reconciles this feature.
        continue;
      }
      // One toast per budget run, shared with the budget view (see acceptedBudgetRun).
      if (transition.feature === "budget") {
        // While this tab's accepted run is open, only its own end or a newer run's ends it. An
        // older run's row (a GET sent before the server wrote "rerunning") is not a finish.
        const acceptedRun = readAcceptedBudgetRun(projectId, documentId);
        if (acceptedRun && !acceptedRunEnd(acceptedRun, transition.to, transition.metadata)) {
          continue;
        }
        if (!claimBudgetRunEnd(projectId, documentId, transition.to, transition.metadata)) continue;
        clearSavedCategoryNames(projectId, documentId);
      }
      notifyTransition(
        queryClient,
        navigate,
        project,
        projectId,
        transition.feature,
        transition.to,
        transition.metadata
      );
    }

    // A budget rerun accepted in this tab can end before this poller saw it running.
    const budgetRow = project.featureStatuses.find((row) => row.feature === "budget");
    if (
      budgetRow &&
      !hasForegroundAiWatcher(projectId, "budget") &&
      endsAcceptedBudgetRun(projectId, documentId, budgetRow.status, budgetRow.metadata) &&
      claimBudgetRunEnd(projectId, documentId, budgetRow.status, budgetRow.metadata)
    ) {
      clearSavedCategoryNames(projectId, documentId);
      notifyTransition(
        queryClient,
        navigate,
        project,
        projectId,
        "budget",
        budgetRow.status,
        budgetRow.metadata
      );
    }

    const stillActive = project.featureStatuses.some((row) => isActiveAiRunStatus(row.status));
    if (stillActive) {
      idlePollsRef.current = 0;
      return;
    }
    // A couple of quiet polls before unwatching guards against races where the
    // optimistic lock cleared just before the server row flipped to rerunning.
    // Counted per completed fetch: an identical refetch keeps the same data object.
    if (lastQuietFetchRef.current === dataUpdatedAt) return;
    lastQuietFetchRef.current = dataUpdatedAt;
    // A new watch also stays for a while, so a slow start request cannot end it before the
    // server writes the running row.
    idlePollsRef.current += 1;
    if (idlePollsRef.current >= 2 && Date.now() - watchedSinceRef.current >= MIN_WATCH_MS) {
      onIdle(projectId);
    }
  }, [dataUpdatedAt, documentId, navigate, onIdle, project, projectId, queryClient]);

  return null;
};

/**
 * The toast for a finished AI run. A budget row can say completed while carrying an error;
 * the budget panel reads that as failed, so this does too.
 */
export const aiRunTransitionToast = (
  feature: string,
  status: string,
  metadata: unknown,
  projectName: string
): { message: string; tone: "error" | "warning" | "success" } => {
  const effectiveStatus =
    feature === "budget" ? resolveEffectiveBudgetFeatureStatus(status, metadata) : status;
  const label = aiRunFeatureLabel(feature);
  if (effectiveStatus !== FEATURE_STATUS_COMPLETED) {
    return {
      message: `${label} failed for ${projectName}. ${aiRunFailureReason(metadata) ?? "You can retry from the project."}`,
      tone: "error",
    };
  }
  const availabilityConflicts =
    feature === "schedule" ? getScheduleAvailabilityConflictCount(metadata) : 0;
  if (availabilityConflicts > 0) {
    return {
      message: `${label} finished for ${projectName}. ${formatAvailabilityConflictSentence(
        availabilityConflicts,
        getScheduleAvailabilityConflictExamples(metadata)
      )}`,
      tone: "warning",
    };
  }
  return { message: `${label} finished for ${projectName}.`, tone: "success" };
};

const notifyTransition = (
  queryClient: ReturnType<typeof useQueryClient>,
  navigate: ReturnType<typeof useNavigate>,
  project: ProjectState,
  projectId: string,
  feature: string,
  status: string,
  metadata?: unknown
) => {
  const toastId = `ai-run-${projectId}-${feature}`;
  toast.dismiss(toastId);

  // Freshness: the panel data changed server-side while nothing was mounted.
  void queryClient.invalidateQueries({ queryKey: projectKeys.detail(projectId) });
  void queryClient.invalidateQueries({ queryKey: sessionKeys.all });
  if (feature === "character_breakdown") {
    void queryClient.invalidateQueries({ queryKey: characterKeys.all });
  } else if (feature === "coverage") {
    void queryClient.invalidateQueries({ queryKey: coverageKeys.all });
  } else if (feature === "budget" || feature === "tax_incentives") {
    void queryClient.invalidateQueries({ queryKey: budgetKeys.all });
  } else if (feature === "schedule") {
    void queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
  } else {
    void queryClient.invalidateQueries({ queryKey: scriptKeys.all });
  }

  const projectName = project.name ? `"${project.name}"` : "your project";
  const workspaceId = project.workspaceId;
  const openPath =
    workspaceId && projectId
      ? `/workspaces/${workspaceId}/projects/${projectId}/${aiRunFeaturePanelPath(feature)}`
      : null;

  const { message, tone } = aiRunTransitionToast(feature, status, metadata, projectName);

  handleToastWithAction(
    message,
    openPath ? (
      <ToastActionButton
        onClick={() => {
          toast.dismiss(toastId);
          navigate(openPath);
        }}
      >
        Open
      </ToastActionButton>
    ) : null,
    tone,
    "bottom-left",
    toastId
  );
};
